/**
 * New Follower Notification Component
 * Displays a notification when someone follows the user
 * New design with brand header and menu
 */

import { EllipsisVertical } from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { getAssetUrl } from "@/utils/assets"
import type { Notification } from "../services/notification.service"

interface NewFollowerNotificationProps {
  notification: Notification
  onRead?: () => void
  onUnread?: () => void
  onNotificationClick?: (notification: Notification) => void
}

function formatFullDate(date: Date): string {
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ]

  const day = date.getDate()
  const month = months[date.getMonth()]
  const year = date.getFullYear()

  const hours = date.getHours()
  const minutes = date.getMinutes().toString().padStart(2, "0")
  const ampm = hours >= 12 ? "PM" : "AM"
  const displayHours = hours % 12 || 12

  return `${month} ${day}, ${year} at ${displayHours}:${minutes} ${ampm}`
}

export function NewFollowerNotification({
  notification,
  onRead,
  onUnread,
  onNotificationClick,
}: NewFollowerNotificationProps) {
  if (!notification.triggeredByUser) {
    return null
  }

  const user = notification.triggeredByUser
  const displayName = user.name || "User"
  const isRead = notification.isRead

  const handleFollowerNameClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    window.location.href = `/profile/${user.id}`
    if (!isRead) {
      onRead?.()
    }
  }

  const handleMarkAsRead = () => {
    onRead?.()
  }

  const handleMarkAsUnread = () => {
    onUnread?.()
  }

  const timeString = formatFullDate(new Date(notification.createdAt))

  return (
    <div
      className={`flex flex-col gap-2 p-4 rounded-md cursor-pointer transition-colors border ${
        !isRead ? "notification-unread" : "notification-read"
      }`}
      onClick={() => {
        onRead?.()
        onNotificationClick?.(notification)
      }}
    >
      {/* Header Row: Logo, Brand, Time, Menu */}
      <div className="relative flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {/* Logo */}
          <img
            src={getAssetUrl("Avatar.png")}
            alt="Rituality"
            className="w-[50px] h-[50px] rounded-full object-cover"
          />

          {/* Brand Name */}
          <div className="flex flex-col gap-[7px]">
            <div className="flex items-center gap-[7px]">
              <span className="font-semibold text-primary text-[14px]">Rituality</span>
              <span className="text-stone text-[14px]">@Rituality</span>
              {/* Time */}
              <span className="text-stone/50 text-[14px]">{timeString}</span>
            </div>

            {/* Message Row */}
            <div className="">
              <span className="text-[14px] text-primary leading-[25px]">
                <span className="font-semibold cursor-pointer" onClick={handleFollowerNameClick}>
                  {displayName}
                </span>{" "}
                started following you
              </span>
            </div>
          </div>
        </div>

        <div className="absolute -top-2 -right-3">
          {/* Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="w-8 h-8 rounded-full flex items-center justify-center transition-colors cursor-pointer"
                onClick={e => e.stopPropagation()}
              >
                <EllipsisVertical className="w-4 h-4 text-stone" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40">
              {isRead ? (
                <DropdownMenuItem
                  onClick={e => {
                    e.stopPropagation()
                    handleMarkAsUnread()
                  }}
                  className="text-[11px] cursor-pointer"
                >
                  Mark as unread
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  onClick={e => {
                    e.stopPropagation()
                    handleMarkAsRead()
                  }}
                  className="text-[11px] cursor-pointer"
                >
                  Mark as read
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  )
}
